import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Archive, ArchiveRestore, ExternalLink, HandHeart, Loader2, MoreHorizontal, Pencil, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { needHref, postHref } from "@/lib/otterAssistRoutes";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { setupFromConfig } from "@/components/social/OtterAssistComposerPanel";

// Otter Assist V1 Phase 7 — one need in the sender's My Needs dashboard (OAD-087 / OAD-091).
//
// Per OAD-087 this card NEVER shows Members Reached, members contacted, a delivery total, a wave
// count or wave progress. The only outreach number is how many people are actively offering to help.

const STATUS_COPY = {
  active: "Active", pending: "Paused", help_received: "Help received",
  closed: "Closed", expired: "Expired", matching: "Finding matches",
  awaiting_confirmation: "Awaiting your decision", no_matches: "No matches yet",
  matching_failed: "Needs attention", admin_paused: "Paused",
  configured: "Not published", admin_cancelled: "Closed",
};
const URGENCY_COPY = {
  immediate: "Immediate — today", asap: "ASAP — within 48 hours", soon: "Soon — within 7 days",
  within_days: "Within a set number of days", flexible: "Flexible — no firm deadline",
};
const CONFIRM_COPY = {
  mark_help_received: {
    title: "Mark this need as help received?",
    body: "This closes the need for new outreach. Your post, comments and messages stay exactly as they are.",
  },
  close: {
    title: "Close this need?",
    body: "Outreach stops and I Can Help is switched off. Your post, comments and messages stay visible.",
  },
};

export function MyNeedCard({ need, onOpenResponders, onEdit }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ["oa-my-needs"] });

  const act = useMutation({
    mutationFn: async (action) =>
      (await api.post(`/otter-assist/assists/${need.assist_id}/status-action`,
        { action, confirm: true })).data,
    onSuccess: () => { toast.success("Updated"); invalidate(); },
    onError: () => toast.error("Something went wrong with Otter Assist. Please try again."),
  });

  const archive = useMutation({
    mutationFn: async (archived) =>
      (await api.post(`/otter-assist/assists/${need.assist_id}/sender-archive`,
        { archived })).data,
    onSuccess: (_d, archived) => {
      toast.success(archived ? "Archived — your history is kept." : "Restored to My Needs.");
      invalidate();
    },
    onError: () => toast.error("Only needs that are no longer asking for help can be archived."),
  });

  const relaunch = useMutation({
    mutationFn: async () => {
      const { data } = await api.get(
        `/otter-assist/assists/${need.assist_id}/relaunch-prefill`);
      await api.put("/otter-assist/composer-state", {
        identity_type: data.identity_type, identity_id: data.identity_id || null,
        post: { body: data.post?.body || "" },
        assist: { ...setupFromConfig(data.config), relaunched_from_assist_id: data.source_assist_id },
      });
      return data;
    },
    onSuccess: () => {
      toast.success("Ready to relaunch — review your need, then post it.");
      navigate("/chatter");
    },
    onError: () => toast.error("Something went wrong with Otter Assist. Please try again."),
  });

  const busy = act.isPending || archive.isPending || relaunch.isPending;
  const text = need.post_text || need.summary || "Your Otter Assist need";
  const long = text.length > 240;

  return (
    <article className="rounded-xl border border-border bg-card p-4"
      data-testid={`my-need-${need.assist_id}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <p className={`text-sm text-foreground/90 ${expanded || !long ? "" : "line-clamp-3"}`}
            data-testid={`my-need-summary-${need.assist_id}`}>
            {expanded || !long ? text : `${text.slice(0, 240)}…`}
          </p>
          {long && (
            <button type="button" onClick={() => setExpanded((v) => !v)}
              className="mt-1 text-[11px] font-[600] text-teal-800 hover:underline"
              data-testid={`my-need-expand-${need.assist_id}`}>
              {expanded ? "Show less" : "Read more"}
            </button>
          )}
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
            <span className="rounded-full bg-secondary px-2 py-0.5 font-[600] text-foreground"
              data-testid={`my-need-status-${need.assist_id}`}>
              {STATUS_COPY[need.status] || need.status}
            </span>
            {need.sender_archived_at && (
              <span className="rounded-full bg-secondary px-2 py-0.5 font-[600] text-muted-foreground"
                data-testid={`my-need-archived-${need.assist_id}`}>Archived</span>
            )}
            {need.urgency && (
              <span data-testid={`my-need-urgency-${need.assist_id}`}>
                {URGENCY_COPY[need.urgency] || need.urgency}
              </span>
            )}
            <span data-testid={`my-need-deadline-${need.assist_id}`}>
              {need.deadline_at
                ? `By ${new Date(need.deadline_at).toLocaleString()}`
                : "No firm deadline"}
            </span>
            {need.post_id && (
              <Link to={needHref(need.assist_id, "my-needs")} className="hover:underline"
                data-testid={`my-need-view-need-${need.assist_id}`}>View Need</Link>
            )}
            {need.post_id && (
              <Link to={postHref(need.post_id, "my-needs")} className="hover:underline"
                data-testid={`my-need-view-post-${need.assist_id}`}>View Post</Link>
            )}
            {need.relaunched_to_assist_id && (
              <span data-testid={`my-need-relaunched-${need.assist_id}`}>Relaunched</span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-between gap-2 sm:justify-start">
          <Button type="button" size="sm" variant={need.offered_count ? "default" : "outline"}
            className="h-8 min-w-0 truncate text-[11px]" disabled={!need.offered_count}
            onClick={() => onOpenResponders(need.assist_id)}
            data-testid={`my-need-offered-${need.assist_id}`}>
            <HandHeart className="mr-1.5 h-3.5 w-3.5" />
            {need.offered_count === 1 ? "1 offered to help" : `${need.offered_count} offered to help`}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button type="button" size="sm" variant="ghost" className="h-8 px-2" disabled={busy}
                aria-label="More actions" data-testid={`my-need-menu-${need.assist_id}`}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <MoreHorizontal className="h-4 w-4" />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {need.can_edit && (
                <DropdownMenuItem onClick={() => onEdit(need)}
                  data-testid={`my-need-edit-${need.assist_id}`}>
                  <Pencil className="mr-2 h-3.5 w-3.5" /> Edit Otter Assist
                </DropdownMenuItem>
              )}
              {need.can_pause && (
                <DropdownMenuItem onClick={() => act.mutate("pause")}
                  data-testid={`my-need-pause-${need.assist_id}`}>Mark pending</DropdownMenuItem>
              )}
              {need.can_resume && (
                <DropdownMenuItem onClick={() => act.mutate("resume")}
                  data-testid={`my-need-resume-${need.assist_id}`}>Resume outreach</DropdownMenuItem>
              )}
              {need.can_mark_help_received && (
                <DropdownMenuItem onClick={() => setConfirm("mark_help_received")}
                  data-testid={`my-need-help-received-${need.assist_id}`}>
                  Help received
                </DropdownMenuItem>
              )}
              {need.can_close && (
                <DropdownMenuItem onClick={() => setConfirm("close")}
                  data-testid={`my-need-close-${need.assist_id}`}>Close</DropdownMenuItem>
              )}
              {need.can_relaunch && (
                <DropdownMenuItem onClick={() => relaunch.mutate()}
                  data-testid={`my-need-relaunch-${need.assist_id}`}>
                  <RefreshCw className="mr-2 h-3.5 w-3.5" /> Edit and relaunch
                </DropdownMenuItem>
              )}
              {need.sender_archived_at ? (
                <DropdownMenuItem onClick={() => archive.mutate(false)}
                  data-testid={`my-need-restore-${need.assist_id}`}>
                  <ArchiveRestore className="mr-2 h-3.5 w-3.5" /> Restore
                </DropdownMenuItem>
              ) : need.can_archive ? (
                <DropdownMenuItem onClick={() => archive.mutate(true)}
                  data-testid={`my-need-archive-${need.assist_id}`}>
                  <Archive className="mr-2 h-3.5 w-3.5" /> Archive
                </DropdownMenuItem>
              ) : null}
              {need.post_id && (
                <DropdownMenuItem asChild>
                  <Link to={needHref(need.assist_id, "my-needs")}
                    data-testid={`my-need-menu-view-need-${need.assist_id}`}>
                    <HandHeart className="mr-2 h-3.5 w-3.5" /> View Need
                  </Link>
                </DropdownMenuItem>
              )}
              {need.post_id && (
                <DropdownMenuItem asChild>
                  <Link to={postHref(need.post_id, "my-needs")}
                    data-testid={`my-need-menu-view-post-${need.assist_id}`}>
                    <ExternalLink className="mr-2 h-3.5 w-3.5" /> View Post
                  </Link>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={(v) => { if (!v) setConfirm(null); }}>
        <AlertDialogContent data-testid={`my-need-confirm-${need.assist_id}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{CONFIRM_COPY[confirm]?.title || "Are you sure?"}</AlertDialogTitle>
            <AlertDialogDescription>{CONFIRM_COPY[confirm]?.body}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`my-need-confirm-cancel-${need.assist_id}`}>
              Keep it open
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { const a = confirm; setConfirm(null); act.mutate(a); }}
              data-testid={`my-need-confirm-ok-${need.assist_id}`}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </article>
  );
}

export default MyNeedCard;
